import { Tabs, TabsList, TabsTrigger } from "@/components/tabs";
import { useEffect, useState } from "react";

import {
  type CellObject,
  type WorkBook,
  type WorkSheet,
  read,
  utils,
} from "@sheet/coredemo";

interface ExcelViewerProps {
  bytes?: ArrayBufferLike | null;
}

interface CellStyle extends React.CSSProperties {
  minWidth?: string;
  height?: string;
}

const getCellDimensions = (
  ws: WorkSheet,
  row: number,
  col: number,
): { width: string; height: string } => {
  const colObj = ws["!cols"]?.[col];
  const rowObj = ws["!rows"]?.[row];

  const width = colObj?.wpx
    ? `${colObj.wpx}px`
    : colObj?.wch
      ? `${colObj.wch * 7}px`
      : "64px";

  const height = rowObj?.hpx
    ? `${rowObj.hpx}px`
    : rowObj?.hpt
      ? `${(rowObj.hpt * 4) / 3}px`
      : "22px";

  return { width, height };
};

const ExcelCell: React.FC<{
  cell: CellObject;
  style: CellStyle;
}> = ({ cell, style }) => {
  const cellText = cell?.v !== undefined ? String(cell.v) : "";

  return (
    <td style={{ ...style, position: "relative", height: 28 }}>{cellText}</td>
  );
};

export const ExcelPreview: React.FC<ExcelViewerProps> = ({ bytes }) => {
  const [workbook, setWorkbook] = useState<WorkBook | null>(null);
  const [selectedSheetName, setSelectedSheetName] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!bytes) return;

    const wb = read(bytes, { cellStyles: true });
    setWorkbook(wb);
    setSelectedSheetName(wb.SheetNames[0]);
  }, [bytes]);

  const renderSheet = (ws: WorkSheet) => {
    if (!ws || !ws["!ref"]) {
      return <div>No data or invalid sheet ref</div>;
    }

    const range = utils.decode_range(ws["!ref"]);
    const rows: JSX.Element[] = [];

    for (let row = range.s.r; row <= range.e.r; row++) {
      const rowCells: JSX.Element[] = [];

      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = utils.encode_cell({ r: row, c: col });
        const cell = ws[cellAddress];
        const { width, height } = getCellDimensions(ws, row, col);

        const style: CellStyle = {
          border: "1px solid #ccc",
          padding: "4px",
          minWidth: width,
          height: height,
        };

        if (cell?.s?.bgColor?.rgb) {
          style.backgroundColor = `#${cell.s.fgColor.rgb}`;
          style.color = `#${cell.s.color.rgb}`;
          // style.fontSize = `${cell.s.sz}px`;
        }

        rowCells.push(<ExcelCell key={col} cell={cell} style={style} />);
      }

      rows.push(<tr key={row}>{rowCells}</tr>);
    }

    return (
      <table style={{ borderCollapse: "collapse" }}>
        <tbody>{rows}</tbody>
      </table>
    );
  };

  if (!workbook) return null;

  return (
    <div className="py-3 gap-x-2 max-w-full">
      <Tabs
        value={selectedSheetName || ""}
        onValueChange={setSelectedSheetName}
      >
        <TabsList>
          {workbook.SheetNames.map((name) => (
            <TabsTrigger value={name} key={name}>
              {name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {selectedSheetName && (
        <div className="mt-2 border max-h-[720px] overflow-auto bg-white">
          {renderSheet(workbook.Sheets[selectedSheetName])}
        </div>
      )}
    </div>
  );
};
